import ArchiveIcon from "@mui/icons-material/Archive";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import WorkIcon from "@mui/icons-material/Work";
import { styled } from "@mui/material";
import Typography from "@mui/material/Typography";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import CompanyColumnCell from "_shared/teams/cells/company-column-cell";
import LeaderColumnCell from "_shared/teams/cells/leader-column-cell";
import MembersColumnCell from "_shared/teams/cells/members-column-cell";
import { listsArchive, STAGE_PARAMETER } from "_shared/teams/teams-list-chips";
import { PageTab } from "_shared/teams/teams-tabs";
import TeamsToolbar, { TeamsInternalActions } from "_shared/teams/teams-toolbar";
import { useTeamsListUrl, useTeamsPageTabs } from "_shared/teams/use-teams-list";
import type { ListChipCounts } from "_shared/_views/list-pages/list-chip-selection";
import _ from "lodash";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import {
  createMRTColumnHelper,
  type MRT_Row as Row,
  type MRT_TableInstance,
  type MRT_TableOptions,
  type MRT_PaginationState as PaginationState,
  type MRT_SortingState as SortingState,
  type MRT_Updater,
} from "material-react-table";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import * as React from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { useSession } from "users/__session/session-context";
import { startDraftProposalFromTeamTemplate } from "work/proposals/_views/drafts/start-draft-proposal-from-template";
import {
  useArchiveTeamTemplateMutation,
  useDeleteTeamTemplateMutation,
  useGetTeamTemplatesQuery,
  useUnarchiveTeamTemplateMutation,
} from "work/team-templates/_api/team-template-api";
import { TeamTemplateOrderField } from "work/team-templates/_api/queries/team-template-order-field";
import TeamTemplateQuery, {
  TeamTemplateFilterParameters,
  TeamTemplateOrderParameters,
} from "work/team-templates/_api/queries/team-template-query";
import TeamTemplate from "work/team-templates/team-template";
import { TemplateUsageKind } from "work/template-usages/_schemas/template-usage-schema";
import TemplateDetailsDrawer from "work/template-usages/_views/template-details-drawer";
import { getSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import useSessionPermissionsLoadState from "permissions/session/use-session-permissions-load-state";
import { MRTNoMemo } from "_shared/_views/tables/table";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import RowActions, { type RowAction } from "_shared/_views/tables/row-actions";
import RowLink from "_shared/_views/tables/row-link";
import SearchMatch from "_shared/_views/tables/search-match";
import { searchTextFieldProps, topToolbarFitsThePage } from "_shared/_views/tables/table-toolbar";
import { LIST_PAGE_SIZE } from "_shared/_views/tables/table-page-size";

const RowTitle = styled(Typography)(() => ({
  fontSize: "1.1em",
  fontWeight: "600",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
const DetailsSummary = styled("dl")(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(1, 2),
  gridTemplateColumns: "auto 1fr",
  margin: 0,
  "& dt": { color: theme.palette.text.secondary },
  "& dd": { margin: 0, minWidth: 0 },
}));

/**
 * Where a row goes, in one place, because the row, the link in it and its Open item have to agree --
 * and because the Archived chip sends the same row somewhere else.
 */
export function teamTemplateDestination(template: TeamTemplate, isArchive: boolean): string | undefined {
  if (!template.id) return undefined;
  return isArchive ? `/teams/archived/view/${template.id.value}` : `/teams/${PageTab.Templates}/edit/${template.id.value}`;
}

const columnHelper = createMRTColumnHelper<TeamTemplate>();

/**
 * Where the columns go when the pane is short of room (#6711, #6718): the people fold onto the
 * template's name, the members first.
 */
const teamTemplateColumnFolds = {
  members: { into: "name", priority: 1 },
  company: { into: "name", priority: 2 },
} as const;

/**
 * The column that can be sorted, and the field the SERVER orders by for it (#5451). Company, Leader
 * and Members are absent on purpose: each is a name the server does not hold -- the template stores
 * people as user ids -- so there is no field to order by and no sort control to offer.
 */
const orderFieldByColumnId: Readonly<Record<string, TeamTemplateOrderField>> = {
  name: TeamTemplateOrderField.Name,
};

/** The table's own state, as the URL holds it (#6712): the search, the sort and the page. */
type TeamTemplateTableState = {
  globalFilter: string;
  sorting: SortingState;
  pagination: PaginationState;
};

export function readTeamTemplateTableState(searchParams: URLSearchParams): TeamTemplateTableState {
  const orderBy = searchParams.get("orderBy")?.toLowerCase();
  const columnId = Object.entries(orderFieldByColumnId).find(([, field]) => field.toLowerCase() === orderBy)?.[0];
  const sorting: SortingState = columnId
    ? [{ id: columnId, desc: searchParams.get("orderDir") === OrderDirection.Descending }]
    : [];
  const { pageIndex, pageSize } = PaginationParameters.fromListPageSearchParams(searchParams);
  return {
    globalFilter: searchParams.get("query") ?? "",
    sorting,
    pagination: { pageIndex, pageSize: searchParams.has("pageSize") ? pageSize : LIST_PAGE_SIZE },
  };
}

/** `current` with the table's state written over it. The Archived chip is the page's, and stays as it is. */
export function writeTeamTemplateTableState(state: TeamTemplateTableState, current: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams();
  for (const stage of current.getAll(STAGE_PARAMETER)) next.append(STAGE_PARAMETER, stage);
  if (state.globalFilter) next.set("query", state.globalFilter);
  const sort = state.sorting.find((each) => orderFieldByColumnId[each.id]);
  if (sort) {
    next.set("orderBy", orderFieldByColumnId[sort.id]);
    next.set("orderDir", sort.desc ? OrderDirection.Descending : OrderDirection.Ascending);
  }
  next.set("pageIndex", String(state.pagination.pageIndex));
  next.set("pageSize", String(state.pagination.pageSize));
  return next;
}

function applyUpdater<TState>(updater: MRT_Updater<TState>, current: TState): TState {
  return typeof updater === "function" ? (updater as (old: TState) => TState)(current) : updater;
}

/** How many members a template has, as it reads folded under its name: "3 members". */
function foldedMembers(template: TeamTemplate): string | undefined {
  if (!(template instanceof TeamTemplate)) return undefined;
  const count = template.members.length;
  if (count < 1) return undefined;
  return count === 1 ? "1 member" : `${count} members`;
}

/**
 * The Templates tab (#6718): the team templates the reader keeps, or with the Archived chip the archived
 * ones. The archive was a tab of its own until #6718.
 */
export default function TeamsTemplatesPage() {
  const navigate = useNavigate();
  const confirm = useConfirmDialog();
  const dispatch = useAttorneyHubDispatch();
  const session = useSession();
  const sessionPermissions = useSelector(getSessionPermissions);
  const permissionsLoadState = useSessionPermissionsLoadState();

  const { searchParams, searchKey, chips, showReaderChange, onTabChange, onChipsChange } = useTeamsListUrl(
    PageTab.Templates,
  );
  const isArchive = listsArchive(chips);
  const tableState = React.useMemo(() => readTeamTemplateTableState(new URLSearchParams(searchKey)), [searchKey]);
  const { globalFilter, sorting, pagination } = tableState;

  // The template whose details panel is open, and the list it was opened from. The page stays mounted
  // across the chip, so the panel is tied to that list: on the other it would describe a row that is
  // not listed, and link to the wrong route for it.
  const [openDetails, setOpenDetails] = React.useState<{ template: TeamTemplate; isArchive: boolean } | undefined>();
  const detailsTemplate = openDetails?.isArchive === isArchive ? openDetails.template : undefined;
  // Named for what they are rather than what the tab is: "No templates" on a page reached from Teams
  // could be any of the three kinds of template in the app.
  const searchSubject = isArchive ? "archived team templates" : "team templates";

  // The search, the order, the page and the archive all go to the server. The server matches the
  // search against the template name, the leader and the members; the browser holds one page.
  const sort = sorting[0];
  const teamTemplatesArgs = React.useMemo(
    () => ({
      query: new TeamTemplateQuery({
        filterParameters: TeamTemplateFilterParameters.create({
          creatorEntityId: session.user?.legalEntityId,
          query: globalFilter.trim() ? globalFilter.trim() : undefined,
          isArchived: isArchive,
        }),
        orderParameters: TeamTemplateOrderParameters.create({
          orderBy: (sort && orderFieldByColumnId[sort.id]) || TeamTemplateOrderField.Name,
          // Ascending where nothing is sorted, which is the order the first page arrives in.
          orderDir: sort?.desc ? OrderDirection.Descending : OrderDirection.Ascending,
        }),
        paginationParameters: PaginationParameters.create(pagination),
      }),
    }),
    [globalFilter, isArchive, pagination, session.user?.legalEntityId, sort],
  );
  // Listing templates asks for teams access at need-to-know read-only, which every member of an
  // organization holds (issue #4651), and an entity to list them for.
  const canList = Boolean(session.user?.legalEntityId) && Boolean(sessionPermissions?.includesAccessToTeams);
  const teamTemplatesQuery = useGetTeamTemplatesQuery(teamTemplatesArgs, { skip: !canList });
  // currentData, not data: data keeps the last answer while another loads, so it would show the
  // templates under the Archived chip.
  const teamTemplatesPage = teamTemplatesQuery.currentData;
  const teamTemplates = React.useMemo(
    () => (teamTemplatesPage ? TeamTemplate.fromJSONArray(teamTemplatesPage.data) : undefined),
    [teamTemplatesPage],
  );
  const isLoadingTeamTemplates = teamTemplatesQuery.isLoading || teamTemplatesQuery.isFetching;
  const errorLoadingTeamTemplates = teamTemplatesQuery.isError;
  // What the failure was, in the response's own words, where there are any (#5235).
  const errorLoadingTeamTemplatesMessage = getErrorMessageFromUnknown(teamTemplatesQuery.error);
  const [deleteTeamTemplate] = useDeleteTeamTemplateMutation();
  const [archiveTeamTemplate] = useArchiveTeamTemplateMutation();
  const [unarchiveTeamTemplate] = useUnarchiveTeamTemplateMutation();

  const { tabs, templateCounts } = useTeamsPageTabs({ activeTab: PageTab.Templates, search: globalFilter, session });
  const chipCounts = React.useMemo<ListChipCounts>(
    () => ({ [STAGE_PARAMETER]: { archived: templateCounts?.archived } }),
    [templateCounts?.archived],
  );

  const teamTemplateUserIds = React.useMemo(
    () =>
      _.uniqBy(
        (teamTemplates ?? []).flatMap((template) => [template.leader.userId, ...template.members.map((m) => m.userId)]),
        (id) => id.value,
      ),
    [teamTemplates],
  );
  useGetIndividualsByUserIdsQuery(
    { userIds: teamTemplateUserIds.map((id) => id.value) },
    { skip: teamTemplateUserIds.length === 0 },
  );

  const updateTableState = React.useCallback(
    (change: Partial<TeamTemplateTableState>) =>
      showReaderChange(writeTeamTemplateTableState({ ...tableState, ...change }, searchParams)),
    [searchParams, showReaderChange, tableState],
  );

  async function handleDeleteTeamTemplate(id: Guid) {
    const response = await confirm({
      title: "Delete?",
      message: `Delete team template?`,
      okButtonText: "Delete",
      cancelButtonText: "Cancel",
    });
    if (response === ConfirmResponse.Cancel) return;

    try {
      await deleteTeamTemplate({ templateId: id.value }).unwrap();
      enqueueSnackbar("Deleted vendor team template", { variant: "success" });
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to delete vendor team template. Please try again", { variant: "error" });
    }
  }

  async function handleArchiveTeamTemplate(id: Guid, archive: boolean) {
    const action = archive ? "Archive" : "Unarchive";
    const response = await confirm({
      title: `${action}?`,
      message: `${action} team template?`,
      okButtonText: action,
      cancelButtonText: "Cancel",
    });
    if (response === ConfirmResponse.Cancel) return;

    try {
      if (archive) await archiveTeamTemplate({ templateId: id.value }).unwrap();
      else await unarchiveTeamTemplate({ templateId: id.value }).unwrap();
      enqueueSnackbar(`${action}d vendor team template`, { variant: "success" });
    } catch (error) {
      console.error(error);
      enqueueSnackbar(`Failed to ${action.toLowerCase()} vendor team template. Please try again`, {
        variant: "error",
      });
    }
  }

  const handleCreateProposalFromTeamTemplate = React.useCallback(
    (template: TeamTemplate) => {
      startDraftProposalFromTeamTemplate({ dispatch, navigate, session, template });
    },
    [dispatch, navigate, session],
  );

  // The search matches the leader's and the members' names, so those cells mark it (#6590).
  const renderLeaderColumn = React.useCallback(
    (team: TeamTemplate, table?: MRT_TableInstance<TeamTemplate>) => (
      <LeaderColumnCell teamTemplate={team} teamUserIds={teamTemplateUserIds} table={table} />
    ),
    [teamTemplateUserIds],
  );

  const renderMembersColumn = React.useCallback(
    (template: TeamTemplate, table?: MRT_TableInstance<TeamTemplate>) => (
      <MembersColumnCell
        teamTemplate={template}
        teamUserIds={teamTemplateUserIds}
        teamName={template.name?.value}
        table={table}
      />
    ),
    [teamTemplateUserIds],
  );

  const renderCompanyColumn = React.useCallback(
    (template: TeamTemplate) => <CompanyColumnCell teamTemplate={template} teamUserIds={teamTemplateUserIds} />,
    [teamTemplateUserIds],
  );

  const columns = React.useMemo(
    () => [
      columnHelper.accessor((team) => team.name?.value ?? "", {
        header: "Name",
        id: "name",
        meta: { kind: "name" },
        Cell: ({ row, table }: { row: Row<TeamTemplate>; table: MRT_TableInstance<TeamTemplate> }) => (
          <RowTitle>
            <RowLink to={teamTemplateDestination(row.original, isArchive)}>
              <SearchMatch table={table} text={row.original.name?.value} />
            </RowLink>
          </RowTitle>
        ),
        enableSorting: Boolean(orderFieldByColumnId.name),
      }),
      columnHelper.accessor((team) => team.leader?.userId.value ?? "", {
        header: "Company",
        id: "company",
        meta: {
          kind: "text",
          fold: {
            ...teamTemplateColumnFolds.company,
            render: (template: TeamTemplate) => (template instanceof TeamTemplate ? renderCompanyColumn(template) : undefined),
          },
        },
        Cell: ({ row }) => renderCompanyColumn(row.original),
        // The search does not reach the company, so it never shows a match (#6590).
        enableGlobalFilter: false,
        enableSorting: false,
      }),
      columnHelper.accessor((team) => team.leader?.userId.value ?? "", {
        header: "Leader",
        id: "leader",
        meta: { kind: "person" },
        Cell: ({ row, table }) => renderLeaderColumn(row.original, table),
        enableSorting: false,
      }),
      columnHelper.accessor((template) => template.members.length, {
        header: "Members",
        id: "members",
        meta: { kind: "people", fold: { ...teamTemplateColumnFolds.members, render: foldedMembers } },
        Cell: ({ row, table }) => renderMembersColumn(row.original, table),
        enableSorting: false,
      }),
    ],
    [isArchive, renderCompanyColumn, renderLeaderColumn, renderMembersColumn],
  );

  const tableData = React.useMemo(() => teamTemplates ?? [], [teamTemplates]);

  const tableOptions: MRT_TableOptions<TeamTemplate> = {
    columns,
    data: tableData,
    enableRowSelection: false,
    enableTableHead: true,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        if (!(row.original instanceof TeamTemplate)) return;
        const destination = teamTemplateDestination(row.original, isArchive);
        if (destination) void navigate(destination);
      },
      sx: { cursor: "pointer" },
    }),
    renderToolbarInternalActions: ({ table }) => <TeamsInternalActions table={table} />,
    renderTopToolbarCustomActions: ({ table }) => (
      <TeamsToolbar
        table={table}
        activeTab={PageTab.Templates}
        tabs={tabs}
        onTabChange={onTabChange}
        chips={chips}
        chipCounts={chipCounts}
        onChipsChange={onChipsChange}
      />
    ),
    // MRT's loading placeholders carry no id, and one key for all of them is a duplicate React key.
    getRowId: (row, index) => row.id?.value ?? `placeholder-${index}`,
    enableRowActions: true,
    enableColumnPinning: true,
    initialState: {
      columnPinning: { right: ["mrt-row-actions"] },
      // The search box belongs in the toolbar rather than behind a magnifier that has to be found first.
      showGlobalFilter: true,
    },
    positionGlobalFilter: "none",
    muiSearchTextFieldProps: ({ table }) => searchTextFieldProps({ searches: searchSubject, table }),
    renderEmptyRowsFallback: emptyRowsFallback({
      errorDescription: errorLoadingTeamTemplatesMessage,
      isError: errorLoadingTeamTemplates,
      isLoading: isLoadingTeamTemplates,
      onRetry: () => void teamTemplatesQuery.refetch(),
      subject: searchSubject,
    }),
    manualFiltering: true,
    // No per-column filters. Name is covered by the search box, and Company, Leader and Members are
    // names the server does not hold, so a filter on them could only narrow the one page in hand (#5451).
    enableColumnFilters: false,
    enableStickyHeader: true,
    enableStickyFooter: true,
    manualPagination: true,
    manualSorting: true,
    // The ⋯ that ends every row, with Open first (#6711).
    renderRowActions: ({ row }) => {
      if (!(row.original instanceof TeamTemplate)) return null;
      const template = row.original;
      const templateName = template.name?.value ?? "team template";
      const actions: RowAction[] = [
        {
          key: "details",
          label: "Details",
          ariaLabel: `Details of ${templateName}`,
          icon: <InfoIcon />,
          onClick: () => setOpenDetails({ template, isArchive }),
        },
      ];

      if (!isArchive) {
        actions.push(
          {
            key: "create-proposal",
            label: "Create Proposal",
            ariaLabel: `Create proposal from ${templateName}`,
            icon: <WorkIcon />,
            onClick: () => handleCreateProposalFromTeamTemplate(template),
          },
          {
            key: "delete",
            label: "Delete",
            ariaLabel: `Delete ${templateName}`,
            icon: <DeleteIcon />,
            destructive: true,
            onClick: () => {
              if (!template.id) return;
              void handleDeleteTeamTemplate(template.id);
            },
          },
        );
      }

      actions.push({
        key: "archive",
        label: isArchive ? "Unarchive" : "Archive",
        ariaLabel: `${isArchive ? "Unarchive" : "Archive"} ${templateName}`,
        icon: isArchive ? <UnarchiveIcon /> : <ArchiveIcon />,
        onClick: () => {
          if (!template.id) return;
          void handleArchiveTeamTemplate(template.id, !isArchive);
        },
      });

      const destination = teamTemplateDestination(template, isArchive);
      return (
        <RowActions
          actions={actions}
          open={destination ? { to: destination } : undefined}
          ariaLabel={`Actions for ${templateName}`}
        />
      );
    },
    muiTableContainerProps: { sx: { flex: 1, minWidth: 0 } },
    muiTablePaperProps: {
      sx: {
        height: "100%",
        display: "flex",
        flexDirection: "column",
        maxWidth: "100%",
        minWidth: 0,
        overflow: "hidden",
      },
    },
    muiTopToolbarProps: { sx: topToolbarFitsThePage },
    // The API's own words where there are any; "Error loading data" stays as the fallback (#5235).
    muiToolbarAlertBannerProps: errorLoadingTeamTemplates
      ? { color: "error", children: errorLoadingTeamTemplatesMessage ?? "Error loading data" }
      : undefined,
    // A search narrows the whole list, so the page the reader was on describes a list that no longer
    // exists. Without the reset, searching from page three asks the server for page three of the matches.
    onGlobalFilterChange: (updater: MRT_Updater<string>) =>
      updateTableState({
        globalFilter: applyUpdater(updater, globalFilter) ?? "",
        pagination: { ...pagination, pageIndex: 0 },
      }),
    onPaginationChange: (updater: MRT_Updater<PaginationState>) =>
      updateTableState({ pagination: applyUpdater(updater, pagination) }),
    onSortingChange: (updater: MRT_Updater<SortingState>) =>
      updateTableState({ sorting: applyUpdater(updater, sorting), pagination: { ...pagination, pageIndex: 0 } }),
    rowCount: teamTemplatesPage?.headers.totalElements ?? 0,
    state: {
      globalFilter,
      isLoading: !teamTemplates?.length && isLoadingTeamTemplates,
      pagination,
      showAlertBanner: errorLoadingTeamTemplates,
      showProgressBars: isLoadingTeamTemplates,
      sorting,
      showSkeletons: !teamTemplates && isLoadingTeamTemplates,
    },
  };

  if (!sessionPermissions) return permissionsLoadState;
  if (!sessionPermissions.includesAccessToTeams) return <NotAuthorized />;

  return (
    <>
      <MRTNoMemo<TeamTemplate>
        options={tableOptions}
        persistColumnVisibilityAs="teams-templates"
        tableKey="teams"
        cards
      />
      <TemplateDetailsDrawer
        templateId={detailsTemplate?.id?.value}
        kind={TemplateUsageKind.Team}
        title={detailsTemplate?.name?.value || "Team template"}
        templateHref={detailsTemplate ? teamTemplateDestination(detailsTemplate, isArchive) : undefined}
        onClose={() => setOpenDetails(undefined)}
        summary={
          detailsTemplate && (
            <DetailsSummary>
              <dt>Company</dt>
              <dd>{renderCompanyColumn(detailsTemplate)}</dd>
              <dt>Leader</dt>
              <dd>{renderLeaderColumn(detailsTemplate)}</dd>
              <dt>Members</dt>
              <dd>{renderMembersColumn(detailsTemplate)}</dd>
            </DetailsSummary>
          )
        }
      />
    </>
  );
}
